import React, { useState, useEffect } from 'react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import planningTaskService, { PlanningTask } from '../../services/planningTaskService';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  PlayArrow as StartIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Add as AddIcon
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const UnidatumProjectManagerPlanningV2: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [tasks, setTasks] = useState<PlanningTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<PlanningTask>>({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTaskData, setNewTaskData] = useState<Partial<PlanningTask>>({
    title: '',
    description: '',
    timeframe: 'daily',
    priority: 'medium',
    category: '',
    status: 'not-started'
  });
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await planningTaskService.getTasks();
      setTasks(response.data || []);
    } catch (error: any) {
      console.error('Error loading tasks:', error);
      showSnackbar(error.response?.data?.error || 'Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDefaults = async () => {
    try {
      setLoading(true);
      await planningTaskService.createUnidatumPMDefaults();
      showSnackbar('Default tasks loaded successfully!', 'success');
      await loadTasks();
    } catch (error: any) {
      console.error('Error loading defaults:', error);
      showSnackbar(error.response?.data?.error || 'Failed to load default tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (task: PlanningTask) => {
    if (editingTaskId === task._id) return;
    setEditingTaskId(task._id || null);
    setEditFormData(task);
  };

  const handleSaveEdit = async () => {
    if (!editingTaskId) return;
    
    try {
      await planningTaskService.updateTask(editingTaskId, editFormData);
      showSnackbar('Task updated successfully!', 'success');
      setEditingTaskId(null);
      setEditFormData({});
      await loadTasks();
    } catch (error: any) {
      console.error('Error updating task:', error);
      showSnackbar(error.response?.data?.error || 'Failed to update task', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditFormData({});
  };

  const handleToggleComplete = async (task: PlanningTask, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      if (task.completed) {
        await planningTaskService.updateTask(task._id!, { completed: false, status: 'in-progress' });
      } else {
        await planningTaskService.completeTask(task._id!);
      }
      showSnackbar(task.completed ? 'Task marked as incomplete' : 'Task completed!', 'success');
      await loadTasks();
    } catch (error: any) {
      console.error('Error toggling task:', error);
      showSnackbar(error.response?.data?.error || 'Failed to update task', 'error');
    }
  };

  const handleStartTask = async (task: PlanningTask, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await planningTaskService.startTask(task._id!);
      showSnackbar('Task started!', 'success');
      await loadTasks();
    } catch (error: any) {
      console.error('Error starting task:', error);
      showSnackbar(error.response?.data?.error || 'Failed to start task', 'error');
    }
  };

  const handleDeleteTask = async (taskId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await planningTaskService.deleteTask(taskId);
      showSnackbar('Task deleted successfully!', 'success');
      await loadTasks();
    } catch (error: any) {
      console.error('Error deleting task:', error);
      showSnackbar(error.response?.data?.error || 'Failed to delete task', 'error');
    }
  };

  const handleOpenCreateDialog = () => {
    setNewTaskData({
      title: '',
      description: '',
      timeframe: tabValue === 0 ? 'daily' : tabValue === 1 ? 'weekly' : tabValue === 2 ? 'monthly' : tabValue === 3 ? 'quarterly' : 'yearly',
      priority: 'medium',
      category: '',
      status: 'not-started'
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewTaskData({
      title: '',
      description: '',
      timeframe: 'daily',
      priority: 'medium',
      category: '',
      status: 'not-started'
    });
  };

  const handleCreateTask = async () => {
    console.log('handleCreateTask called with data:', newTaskData);
    
    if (!newTaskData.title || !newTaskData.description || !newTaskData.category) {
      console.log('Validation failed:', { 
        title: newTaskData.title, 
        description: newTaskData.description, 
        category: newTaskData.category 
      });
      showSnackbar('Please fill in all required fields (Title, Description, Category)', 'error');
      return;
    }

    try {
      console.log('Calling createTask API...');
      const response = await planningTaskService.createTask(newTaskData);
      console.log('Task created successfully:', response);
      showSnackbar('Task created successfully!', 'success');
      handleCloseCreateDialog();
      await loadTasks();
    } catch (error: any) {
      console.error('Error creating task:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create task';
      showSnackbar(errorMessage, 'error');
    }
  };

  const getTasksByTimeframe = (timeframe: string) => {
    return tasks.filter(task => task.timeframe === timeframe);
  };

  const dailyTasks = getTasksByTimeframe('daily');
  const weeklyTasks = getTasksByTimeframe('weekly');
  const monthlyTasks = getTasksByTimeframe('monthly');
  const quarterlyTasks = getTasksByTimeframe('quarterly');
  const yearlyTasks = getTasksByTimeframe('yearly');

  const priorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#B31B1B';
      case 'medium': return '#FF8C00';
      case 'low': return '#2E7D32';
      default: return '#666';
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#2E7D32';
      case 'in-progress': return '#0071BC';
      case 'testing': return '#FF8C00';
      case 'blocked': return '#B31B1B';
      default: return '#666';
    }
  };

  const renderTaskCard = (task: PlanningTask) => {
    const isEditing = editingTaskId === task._id;

    if (isEditing) {
      return (
        <Card key={task._id} sx={{ borderLeft: `4px solid ${priorityColor(task.priority)}`, mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editFormData.title || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={editFormData.description || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={editFormData.priority || 'medium'}
                    label="Priority"
                    onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value as any })}
                  >
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editFormData.status || 'not-started'}
                    label="Status"
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                  >
                    <MenuItem value="not-started">Not Started</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="testing">Testing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="blocked">Blocked</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Category"
                  value={editFormData.category || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Hours"
                  type="number"
                  value={editFormData.estimatedHours || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, estimatedHours: parseFloat(e.target.value) })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Actual Hours"
                  type="number"
                  value={editFormData.actualHours || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, actualHours: parseFloat(e.target.value) })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={editFormData.notes || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveEdit}
                    sx={{ bgcolor: '#003366' }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        key={task._id}
        onClick={() => handleCardClick(task)}
        sx={{
          borderLeft: `4px solid ${priorityColor(task.priority)}`,
          mb: 2,
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <IconButton
                  size="small"
                  onClick={(e) => handleToggleComplete(task, e)}
                  sx={{ mr: 1 }}
                >
                  {task.completed ? (
                    <CheckIcon sx={{ color: '#2E7D32' }} />
                  ) : (
                    <UncheckedIcon />
                  )}
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#003366',
                    fontWeight: 600,
                    textDecoration: task.completed ? 'line-through' : 'none',
                    flex: 1
                  }}
                >
                  {task.title}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 5, mb: 1 }}>
                {task.description}
              </Typography>
              <Box sx={{ ml: 5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label={task.priority.toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: priorityColor(task.priority),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }}
                />
                <Chip
                  label={task.status.replace('-', ' ').toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: statusColor(task.status),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }}
                />
                <Chip
                  label={task.category}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: '#003366',
                    color: '#003366',
                    fontSize: '0.7rem'
                  }}
                />
                {task.estimatedHours && (
                  <Chip
                    label={`Est: ${task.estimatedHours}h`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
                {task.actualHours && (
                  <Chip
                    label={`Actual: ${task.actualHours}h`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
              {task.status === 'not-started' && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => handleStartTask(task, e)}
                  title="Start Task"
                >
                  <StartIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                color="error"
                onClick={(e) => handleDeleteTask(task._id!, e)}
                title="Delete Task"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderTaskList = (taskList: PlanningTask[], timeframe: string) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (taskList.length === 0) {
      return (
        <Alert severity="info">
          No {timeframe} tasks found. Click "Load Default Tasks" to get started!
        </Alert>
      );
    }

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>{timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} Planning</strong> - Click any card to edit. {taskList.length} task{taskList.length !== 1 ? 's' : ''} found.
        </Alert>
        {taskList.map(task => renderTaskCard(task))}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 4, mb: 3, bgcolor: '#003366', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: 'white'
                }}
                aria-label="Unidatum Project Manager Planning"
              >
                Unidatum Project Manager Planning
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'white'
                }}
                aria-label="Daily, Weekly, Monthly, Quarterly and Yearly Deliverables"
              >
                Daily, Weekly, Monthly, Quarterly & Yearly Deliverables
              </Typography>
              <Chip
                label="DEV ONLY - API Integrated"
                sx={{
                  mt: 2,
                  bgcolor: '#B31B1B',
                  color: 'white',
                  fontWeight: 600
                }}
                aria-label="Development only API integrated tool"
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
                sx={{ bgcolor: '#2E7D32', color: 'white', '&:hover': { bgcolor: '#1B5E20' } }}
              >
                Create New Task
              </Button>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={loadTasks}
                sx={{ bgcolor: 'white', color: '#003366', '&:hover': { bgcolor: '#f0f0f0' } }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={handleLoadDefaults}
                sx={{ bgcolor: '#B31B1B', color: 'white', '&:hover': { bgcolor: '#8B1515' } }}
              >
                Load Default Tasks
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 600 },
              '& .Mui-selected': { color: '#003366' }
            }}
          >
            <Tab icon={<CalendarIcon />} label="Daily" />
            <Tab icon={<CalendarIcon />} label="Weekly" />
            <Tab icon={<CalendarIcon />} label="Monthly" />
            <Tab icon={<TrendingIcon />} label="Quarterly" />
            <Tab icon={<TrendingIcon />} label="Yearly" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            <TabPanel value={tabValue} index={0}>
              {renderTaskList(dailyTasks, 'daily')}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderTaskList(weeklyTasks, 'weekly')}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderTaskList(monthlyTasks, 'monthly')}
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              {renderTaskList(quarterlyTasks, 'quarterly')}
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
              {renderTaskList(yearlyTasks, 'yearly')}
            </TabPanel>
          </Box>
        </Paper>

        {/* Quick Stats */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#003366', color: 'white', textAlign: 'center', p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>{dailyTasks.length}</Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>Daily Tasks</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#0071BC', color: 'white', textAlign: 'center', p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>{weeklyTasks.length}</Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>Weekly Tasks</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#FF8C00', color: 'white', textAlign: 'center', p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>{monthlyTasks.length}</Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>Monthly Tasks</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#2E7D32', color: 'white', textAlign: 'center', p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>{quarterlyTasks.length + yearlyTasks.length}</Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>Strategic Tasks</Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Create Task Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={handleCloseCreateDialog}
          maxWidth="md"
          fullWidth
          aria-labelledby="create-task-dialog-title"
        >
          <DialogTitle
            id="create-task-dialog-title"
            sx={{ bgcolor: '#003366', color: 'white', fontWeight: 700 }}
          >
            Create New Task
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title *"
                  value={newTaskData.title || ''}
                  onChange={(e) => setNewTaskData({ ...newTaskData, title: e.target.value })}
                  required
                  aria-label="Task title"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description *"
                  multiline
                  rows={3}
                  value={newTaskData.description || ''}
                  onChange={(e) => setNewTaskData({ ...newTaskData, description: e.target.value })}
                  required
                  aria-label="Task description"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Timeframe *</InputLabel>
                  <Select
                    value={newTaskData.timeframe || 'daily'}
                    label="Timeframe *"
                    onChange={(e) => setNewTaskData({ ...newTaskData, timeframe: e.target.value as any })}
                    aria-label="Task timeframe"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Priority *</InputLabel>
                  <Select
                    value={newTaskData.priority || 'medium'}
                    label="Priority *"
                    onChange={(e) => setNewTaskData({ ...newTaskData, priority: e.target.value as any })}
                    aria-label="Task priority"
                  >
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Category *"
                  value={newTaskData.category || ''}
                  onChange={(e) => setNewTaskData({ ...newTaskData, category: e.target.value })}
                  required
                  aria-label="Task category"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Hours"
                  type="number"
                  value={newTaskData.estimatedHours || ''}
                  onChange={(e) => setNewTaskData({ ...newTaskData, estimatedHours: parseFloat(e.target.value) })}
                  aria-label="Estimated hours"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newTaskData.status || 'not-started'}
                    label="Status"
                    onChange={(e) => setNewTaskData({ ...newTaskData, status: e.target.value as any })}
                    aria-label="Task status"
                  >
                    <MenuItem value="not-started">Not Started</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="testing">Testing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="blocked">Blocked</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={newTaskData.notes || ''}
                  onChange={(e) => setNewTaskData({ ...newTaskData, notes: e.target.value })}
                  aria-label="Task notes"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseCreateDialog}
              variant="outlined"
              aria-label="Cancel creating task"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}
              aria-label="Create task"
            >
              Create Task
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UnidatumProjectManagerPlanningV2;
